import { useCallback, useEffect, useRef, useState } from 'react'
import { Heading, Subheading } from '@/components/base/heading'
import {
  addDays,
  addWeeks,
  differenceInDays,
  endOfDay,
  endOfWeek,
  format,
  startOfDay,
  startOfWeek,
  subWeeks
} from 'date-fns'
import { Button } from '@/components/base/button'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid'
import { Text } from '@/components/base/text'
import { CalendarCard as EventCalendarCard } from '@/components/event/calendar_card'
import { CalendarCard as ShiftCalendarCard } from '@/components/shift/calendar_card'
import { useQuery } from '@apollo/client'
import { CalendarQuery } from './__generated__/calendar_query'
import { CalendarQuery as Query } from './calendar_query'
import { Inline as Error } from '@/components/error'
import { BeatLoader } from 'react-spinners'

type Event = CalendarQuery['events']['nodes'][0]
type Shift = CalendarQuery['currentUser']['shifts'][0]

const defaultDate = () => startOfWeek(startOfDay(new Date))
const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

export const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(defaultDate())
  const [events, setEvents] = useState<Event[][]>([])
  const [shifts, setShifts] = useState<Shift[][]>([])
  const [loading, setLoading] = useState(false)
  const { data, loading: queryLoading, error, refetch } = useQuery<CalendarQuery>(Query)

  useEffect(() => setLoading(queryLoading || true), [queryLoading])

  const populateCalendar = useCallback(<T,>(data: T[], start: (item: T) => string, finish: (item: T) => string) => {
    const indexedData = new Array(7)
      .fill(0)
      .map(() => new Array<T>)
    data.forEach(item => {
      const index = differenceInDays(startOfDay(start(item)), currentDate)

      if (index >= 0 && index <= 6) indexedData[index].push(item)

      const totalDays = differenceInDays(startOfDay(finish(item)), startOfDay(start(item)))

      for (let i = 1; i <= totalDays; i++) if (index + i >= 0 && index + i <= 6) indexedData[index + i].push(item)
    })
    return indexedData
  }, [currentDate])

  const populateEvents = useCallback((events: Event[]) => {
    setEvents(populateCalendar<Event>(events, event => event.dateOut, event => event.dateReturn))
  }, [populateCalendar])

  const populateShifts = useCallback((shifts: Shift[]) => {
    setShifts(populateCalendar<Shift>(shifts, shift => shift.start, shift => shift.finish))
  }, [populateCalendar])

  useEffect(() => {
    refetch({ eventFilters: { dateOutLteq: endOfWeek(endOfDay(currentDate)), dateReturnGteq: currentDate } })
  }, [currentDate, refetch])

  const timeout = useRef<NodeJS.Timeout>(null)

  useEffect(() => {
    if (!data) return

    if (timeout.current) clearTimeout(timeout.current)

    setLoading(true)
    populateEvents(data.events.nodes)
    populateShifts(data.currentUser.shifts)
    timeout.current = setTimeout(() => setLoading(false), 250)
  }, [currentDate, data, populateEvents, populateShifts])

  if (error) return <Error message={error.message} />

  return (
    <>
      <div
        className={`rounded-lg border border-zinc-950/10 dark:border-white/10 divide-y divide-zinc-950/10
                    dark:divide-white/10`}
      >
        <div className="flex items-center justify-between p-4">
          <Heading className="pl-2">{ format(currentDate, 'do MMMM yyyy') }</Heading>
          { loading && <BeatLoader color="lightgray" size={18} /> }
          <div className="flex flex-row items-center">
            <Button plain onClick={() => setCurrentDate(date => subWeeks(date, 1))}><ChevronLeftIcon /></Button>
            <Button plain onClick={() => setCurrentDate(defaultDate())}>Today</Button>
            <Button plain onClick={() => setCurrentDate(date => addWeeks(date, 1))}><ChevronRightIcon /></Button>
          </div>
        </div>
        <div className="grid grid-cols-8 text-center gap-2">
          <div />
          { days.map((day, index) => (
            <div key={day} className="flex flex-col items-center py-2">
              <Text>{ format(addDays(currentDate, index), 'do') }</Text>
              <Text>{ day }</Text>
            </div>
          )) }
        </div>
        <div className="grid grid-cols-8 text-center items-center py-4 gap-2">
          <Subheading>Events</Subheading>
          { events.map((day, index) => (
            <div key={index} className="flex flex-wrap justify-center gap-2">
              { day.map(event => <EventCalendarCard key={event.id} event={event} />) }
            </div>
          )) }
        </div>
        <div className="grid grid-cols-8 text-center items-center py-4 gap-2">
          <Subheading>Shifts</Subheading>
          { shifts.map((day, index) => (
            <div key={index} className="flex flex-wrap justify-center gap-2">
              { day.map(shift => <ShiftCalendarCard key={shift.id} shift={shift} />) }
            </div>
          )) }
        </div>
        <div className="grid grid-cols-8 text-center items-center py-4 gap-2">
          <Subheading>Maintenance</Subheading>
        </div>
      </div>
    </>
  )
}
